import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBooking } from './BookingContext';
import { X, ChevronRight, Calendar, Clock, Check, ArrowLeft } from 'lucide-react';
type Step = 'activate' | 'reasoning' | 'summary' | 'booking' | 'confirmed';
const industries = ['Hospitality', 'Conveyancing', 'Renewables', 'Maritime', 'Field Service', 'Other'];
const industryWorkflows: Record<string, string[]> = {
  Hospitality: ['Reservation management', 'Guest messaging', 'Service recovery', 'Intelligent upsell'],
  Conveyancing: ['Document automation', 'Client updates', 'Settlement preparation', 'Compliance checking'],
  Renewables: ['Predictive maintenance', 'Fault triage', 'Technician dispatch', 'Performance reporting'],
  Maritime: ['Fleet coordination', 'Vessel reporting', 'Compliance management', 'Port orchestration'],
  'Field Service': ['Intelligent dispatch', 'Work order automation', 'Technician enablement', 'Parts inventory'],
  Other: ['Workflow mapping', 'Process automation', 'Integration design', 'Custom agent configuration']
};
const industryFocus: Record<string, string> = {
  Hospitality: 'Guest experience automation and revenue optimisation',
  Conveyancing: 'Document workflow acceleration and client communication',
  Renewables: 'Asset uptime maximisation and dispatch efficiency',
  Maritime: 'Fleet intelligence and compliance automation',
  'Field Service': 'Dispatch optimisation and first-time fix rate improvement',
  Other: 'Custom workflow analysis and agent architecture design'
};
const reasoningSteps = ['Loading industry workflows', 'Applying operational context', 'Mapping bottlenecks', 'Preparing tailored demo'];
const timeSlots = [{
  day: 'Tomorrow',
  slots: ['10:00 AM', '2:00 PM', '4:00 PM']
}, {
  day: 'Wednesday',
  slots: ['9:00 AM', '11:00 AM', '3:00 PM']
}, {
  day: 'Thursday',
  slots: ['10:00 AM', '1:00 PM', '4:30 PM']
}];
// Particles for background
const particles = Array.from({
  length: 20
}).map((_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  duration: 12 + Math.random() * 20,
  delay: Math.random() * 5
}));
export function BookingExperience() {
  const {
    isOpen,
    closeBooking
  } = useBooking();
  const [step, setStep] = useState<Step>('activate');
  const [selectedIndustry, setSelectedIndustry] = useState<string | null>(null);
  const [reasoningIndex, setReasoningIndex] = useState(0);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Reset state when opening
  useEffect(() => {
    if (isOpen) {
      setStep('activate');
      setSelectedIndustry(null);
      setReasoningIndex(0);
      setSelectedDay(null);
      setSelectedSlot(null);
      setName('');
      setEmail('');
      setIsSubmitting(false);
    }
  }, [isOpen]);
  // Handle industry selection → reasoning sequence
  const handleIndustrySelect = useCallback((industry: string) => {
    setSelectedIndustry(industry);
    setStep('reasoning');
    setReasoningIndex(0);
    reasoningSteps.forEach((_, index) => {
      setTimeout(() => {
        setReasoningIndex(index + 1);
        if (index === reasoningSteps.length - 1) {
          setTimeout(() => setStep('summary'), 1200);
        }
      }, (index + 1) * 1000);
    });
  }, []);
  // Handle booking submission
  const handleSubmit = useCallback(() => {
    if (!name.trim() || !email.trim() || !selectedDay || !selectedSlot) return;
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setStep('confirmed');
    }, 1500);
  }, [name, email, selectedDay, selectedSlot]);
  // Escape key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeBooking();
    };
    if (isOpen) window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeBooking]);
  return <AnimatePresence data-id="element-140">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.5
    }} className="fixed inset-0 z-[100] bg-black flex items-center justify-center overflow-y-auto" data-id="element-141">
          {/* Background particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-10" data-id="element-142">
            {particles.map(p => <motion.div key={p.id} className="absolute w-px h-px bg-white rounded-full" style={{
          left: `${p.x}%`,
          top: `${p.y}%`
        }} animate={{
          y: [0, -80, 0],
          x: [0, Math.random() * 30 - 15, 0],
          opacity: [0, 0.6, 0]
        }} transition={{
          duration: p.duration,
          repeat: Infinity,
          delay: p.delay,
          ease: 'linear'
        }} data-id="element-143" />)}
          </div>

          {/* Subtle grid */}
          <div className="absolute inset-0 opacity-5 pointer-events-none" data-id="element-144">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" data-id="element-145">
              <defs data-id="element-146">
                <pattern id="booking-grid" width="60" height="60" patternUnits="userSpaceOnUse" data-id="element-147">
                  <path d="M 60 0 L 0 0 0 60" fill="none" stroke="white" strokeWidth="0.5" data-id="element-148" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#booking-grid)" data-id="element-149" />
            </svg>
          </div>

          {/* Close button */}
          <motion.button initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.3
      }} onClick={closeBooking} className="absolute top-6 right-6 z-50 w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:border-white/30 transition-all" data-id="element-150">
            <X className="w-5 h-5" data-id="element-151" />
          </motion.button>

          {/* Content */}
          <div className="relative z-10 w-full max-w-2xl mx-auto px-6 py-20" data-id="element-152">
            <AnimatePresence mode="wait" data-id="element-153">
              {/* STEP 1: Activate + Industry Selection */}
              {step === 'activate' && <motion.div key="activate" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -30
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} className="flex flex-col items-center text-center" data-id="element-154">
                  {/* Activation animation */}
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="w-16 h-16 rounded-full bg-brand-orange/10 border border-brand-orange/30 flex items-center justify-center mb-8 relative" data-id="element-155">
                    <div className="w-3 h-3 rounded-full bg-brand-orange" data-id="element-156" />
                    <motion.div className="absolute inset-0 rounded-full border border-brand-orange/50" animate={{
                scale: [1, 1.5, 1],
                opacity: [1, 0, 1]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} data-id="element-157" />
                  </motion.div>

                  <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.4
            }} className="text-sm font-mono text-brand-orange uppercase tracking-widest mb-4" data-id="element-158">
                    Aijentik Agent Activated.
                  </motion.p>

                  <motion.h2 initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.7
            }} className="font-display text-3xl md:text-4xl text-white mb-12 tracking-tight" data-id="element-159">
                    What industry are you in?
                  </motion.h2>

                  <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 1
            }} className="grid grid-cols-2 md:grid-cols-3 gap-3 w-full max-w-lg" data-id="element-160">
                    {industries.map((industry, i) => <motion.button key={industry} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 1 + i * 0.1
              }} whileHover={{
                scale: 1.03
              }} whileTap={{
                scale: 0.97
              }} onClick={() => handleIndustrySelect(industry)} className="px-6 py-4 bg-[#0A0A0A] border border-white/10 rounded-2xl text-sm font-medium text-gray-300 hover:text-white hover:border-brand-orange/50 hover:bg-brand-orange/5 transition-all duration-300" data-id="element-161">
                        {industry}
                      </motion.button>)}
                  </motion.div>
                </motion.div>}

              {/* STEP 2: Reasoning */}
              {step === 'reasoning' && <motion.div key="reasoning" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -30
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} className="flex flex-col items-center text-center" data-id="element-162">
                  <motion.div className="w-16 h-16 rounded-full bg-brand-orange/10 border border-brand-orange/30 flex items-center justify-center mb-10 relative" data-id="element-163">
                    <motion.div className="w-3 h-3 rounded-full bg-brand-orange" animate={{
                scale: [1, 1.3, 1]
              }} transition={{
                duration: 1,
                repeat: Infinity
              }} data-id="element-164" />
                  </motion.div>

                  <p className="text-sm font-mono text-gray-500 uppercase tracking-widest mb-2" data-id="element-165">
                    Analysing
                  </p>
                  <h3 className="font-display text-2xl text-white mb-12 tracking-tight" data-id="element-166">
                    {selectedIndustry}
                  </h3>

                  <div className="space-y-6 w-full max-w-sm" data-id="element-167">
                    {reasoningSteps.map((stepText, index) => {
                const isActive = reasoningIndex === index + 1;
                const isCompleted = reasoningIndex > index + 1;
                const isPending = reasoningIndex <= index;
                return <motion.div key={index} initial={{
                  opacity: 0.2
                }} animate={{
                  opacity: isActive || isCompleted ? 1 : 0.2
                }} className="flex items-center gap-4" data-id="element-168">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${isActive ? 'bg-brand-orange/20 border border-brand-orange/50' : isCompleted ? 'bg-white/10 border border-white/20' : 'bg-[#111] border border-white/5'}`} data-id="element-169">
                            {isCompleted ? <Check className="w-3 h-3 text-gray-400" data-id="element-170" /> : isActive ? <motion.div className="w-2 h-2 rounded-full bg-brand-orange" animate={{
                      scale: [1, 1.3, 1]
                    }} transition={{
                      duration: 0.8,
                      repeat: Infinity
                    }} data-id="element-171" /> : <div className="w-1.5 h-1.5 rounded-full bg-gray-700" data-id="element-172" />}
                          </div>
                          <span className={`text-sm font-medium transition-colors duration-500 ${isActive ? 'text-white' : isCompleted ? 'text-gray-400' : 'text-gray-700'}`} data-id="element-173">
                            {stepText}
                          </span>
                          {isActive && <motion.div initial={{
                    width: 0
                  }} animate={{
                    width: '100%'
                  }} transition={{
                    duration: 0.8,
                    ease: 'linear'
                  }} className="flex-1 h-px bg-brand-orange/50" data-id="element-174" />}
                        </motion.div>;
              })}
                  </div>
                </motion.div>}

              {/* STEP 3: Summary */}
              {step === 'summary' && selectedIndustry && <motion.div key="summary" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -30
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} className="flex flex-col items-center text-center" data-id="element-175">
                  <motion.div initial={{
              scale: 0.8
            }} animate={{
              scale: 1
            }} className="w-12 h-12 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mb-8" data-id="element-176">
                    <Check className="w-5 h-5 text-green-400" data-id="element-177" />
                  </motion.div>

                  <p className="text-sm font-mono text-brand-orange uppercase tracking-widest mb-3" data-id="element-178">
                    Analysis Complete
                  </p>
                  <h3 className="font-display text-2xl md:text-3xl text-white mb-10 tracking-tight" data-id="element-179">
                    We'll tailor this demo to your business.
                  </h3>

                  <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-8 w-full max-w-md text-left mb-10" data-id="element-180">
                    <div className="space-y-6" data-id="element-181">
                      <div data-id="element-182">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-widest mb-2" data-id="element-183">
                          Industry
                        </p>
                        <p className="text-white font-medium" data-id="element-184">
                          {selectedIndustry}
                        </p>
                      </div>
                      <div data-id="element-185">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-widest mb-2" data-id="element-186">
                          Key Workflows
                        </p>
                        <div className="flex flex-wrap gap-2" data-id="element-187">
                          {industryWorkflows[selectedIndustry]?.map((w, i) => <span key={i} className="text-xs text-gray-300 bg-white/5 border border-white/5 px-3 py-1 rounded-full" data-id="element-188">
                              {w}
                            </span>)}
                        </div>
                      </div>
                      <div data-id="element-189">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-widest mb-2" data-id="element-190">
                          Estimated Demo Focus
                        </p>
                        <p className="text-gray-300 text-sm" data-id="element-191">
                          {industryFocus[selectedIndustry]}
                        </p>
                      </div>
                    </div>
                  </div>

                  <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={() => setStep('booking')} className="px-8 py-4 bg-brand-orange hover:bg-brand-deep-orange text-white font-medium rounded-full transition-colors duration-300 flex items-center gap-2 shadow-[0_0_20px_rgba(219,127,74,0.2)] hover:shadow-[0_0_40px_rgba(219,127,74,0.4)]" data-id="element-192">
                    Schedule Your Demo
                    <ChevronRight className="w-4 h-4" data-id="element-193" />
                  </motion.button>
                </motion.div>}

              {/* STEP 4: Booking Interface */}
              {step === 'booking' && <motion.div key="booking" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -30
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} className="flex flex-col items-center" data-id="element-194">
                  <button onClick={() => setStep('summary')} className="self-start flex items-center gap-2 text-sm text-gray-500 hover:text-white transition-colors mb-8" data-id="element-195">
                    <ArrowLeft className="w-4 h-4" data-id="element-196" />
                    Back
                  </button>

                  <Calendar className="w-8 h-8 text-brand-orange mb-6" data-id="element-197" />
                  <h3 className="font-display text-2xl md:text-3xl text-white mb-10 tracking-tight text-center" data-id="element-198">
                    Choose a Time
                  </h3>

                  {/* Time Slots */}
                  <div className="w-full max-w-md space-y-6 mb-10" data-id="element-199">
                    {timeSlots.map(dayGroup => <div key={dayGroup.day} data-id="element-200">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-widest mb-3" data-id="element-201">
                          {dayGroup.day}
                        </p>
                        <div className="flex gap-3" data-id="element-202">
                          {dayGroup.slots.map(slot => {
                    const isSelected = selectedDay === dayGroup.day && selectedSlot === slot;
                    return <button key={slot} onClick={() => {
                      setSelectedDay(dayGroup.day);
                      setSelectedSlot(slot);
                    }} className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all duration-300 border ${isSelected ? 'bg-brand-orange/10 border-brand-orange/50 text-white shadow-[0_0_15px_rgba(219,127,74,0.15)]' : 'bg-[#0A0A0A] border-white/10 text-gray-400 hover:border-white/20 hover:text-white'}`} data-id="element-203">
                                <Clock className="w-3 h-3 inline mr-1.5 -mt-0.5" data-id="element-204" />
                                {slot}
                              </button>;
                  })}
                        </div>
                      </div>)}
                  </div>

                  {/* Form Fields */}
                  <div className="w-full max-w-md space-y-4 mb-10" data-id="element-205">
                    <input type="text" placeholder="Your name" value={name} onChange={e => setName(e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-5 py-4 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-brand-orange/50 transition-colors" data-id="element-206" />
                    <input type="email" placeholder="Work email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-5 py-4 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-brand-orange/50 transition-colors" data-id="element-207" />
                  </div>

                  <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={handleSubmit} disabled={!name.trim() || !email.trim() || !selectedDay || !selectedSlot || isSubmitting} className="px-8 py-4 bg-brand-orange hover:bg-brand-deep-orange disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium rounded-full transition-all duration-300 flex items-center gap-2 shadow-[0_0_20px_rgba(219,127,74,0.2)] hover:shadow-[0_0_40px_rgba(219,127,74,0.4)]" data-id="element-208">
                    {isSubmitting ? <motion.div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full" animate={{
                rotate: 360
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-209" /> : <>
                        Confirm Demo
                        <ChevronRight className="w-4 h-4" data-id="element-210" />
                      </>}
                  </motion.button>
                </motion.div>}

              {/* STEP 5: Confirmation */}
              {step === 'confirmed' && <motion.div key="confirmed" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="flex flex-col items-center text-center" data-id="element-211">
                  <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} transition={{
              duration: 0.5,
              type: 'spring',
              bounce: 0.3
            }} className="w-20 h-20 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mb-10 relative" data-id="element-212">
                    <Check className="w-8 h-8 text-green-400" data-id="element-213" />
                    <motion.div className="absolute inset-0 rounded-full bg-green-500/10" animate={{
                scale: [1, 1.5],
                opacity: [0.5, 0]
              }} transition={{
                duration: 1.5,
                repeat: Infinity
              }} data-id="element-214" />
                  </motion.div>

                  <motion.h3 initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="font-display text-3xl md:text-4xl text-white mb-4 tracking-tight" data-id="element-215">
                    Your demo is confirmed.
                  </motion.h3>

                  <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.6
            }} className="text-gray-400 text-lg mb-4" data-id="element-216">
                    We're preparing your workflows now.
                  </motion.p>

                  <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.9
            }} className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 w-full max-w-sm mb-12" data-id="element-217">
                    <div className="space-y-3 text-sm" data-id="element-218">
                      <div className="flex justify-between" data-id="element-219">
                        <span className="text-gray-500" data-id="element-220">Industry</span>
                        <span className="text-white font-medium" data-id="element-221">
                          {selectedIndustry}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-222">
                        <span className="text-gray-500" data-id="element-223">Date</span>
                        <span className="text-white font-medium" data-id="element-224">
                          {selectedDay}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-225">
                        <span className="text-gray-500" data-id="element-226">Time</span>
                        <span className="text-white font-medium" data-id="element-227">
                          {selectedSlot}
                        </span>
                      </div>
                    </div>
                  </motion.div>

                  <motion.button initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 1.2
            }} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={closeBooking} className="px-8 py-4 bg-white/5 border border-white/10 hover:border-white/30 text-white font-medium rounded-full transition-all duration-300" data-id="element-228">
                    Return to Site
                  </motion.button>
                </motion.div>}
            </AnimatePresence>
          </div>
        </motion.div>}
    </AnimatePresence>;
}